import { useCategories } from "../hooks/useCategories.ts";
import { useProjects } from "../hooks/useProjects.ts";
import { useAllTools } from "../hooks/useAlternatives.ts";
import { useDocumentMeta } from "../hooks/useDocumentMeta.ts";
import { StatsBar } from "../components/StatsBar.tsx";
import { CategoryGrid } from "../components/CategoryGrid.tsx";
import { ProjectGrid } from "../components/ProjectGrid.tsx";
import { Spinner } from "../components/Spinner.tsx";
import { navigate } from "../lib/router.ts";

const ASCII_ART = `
 ████████╗██╗  ██╗███████╗██████╗ ███████╗ ███████╗     █████╗ ███╗   ██╗
 ╚══██╔══╝██║  ██║██╔════╝██╔══██╗██╔════╝ ██╔════╝    ██╔══██╗████╗  ██║
    ██║   ███████║█████╗  ██████╔╝█████╗   ███████╗    ███████║██╔██╗ ██║
    ██║   ██╔══██║██╔══╝  ██╔══██╗██╔══╝   ╚════██║    ██╔══██║██║╚██╗██║
    ██║   ██║  ██║███████╗██║  ██║███████╗  ███████║    ██║  ██║██║ ╚████║
    ╚═╝   ╚═╝  ╚═╝╚══════╝╚═╝  ╚═╝╚══════╝ ╚══════╝    ╚═╝  ╚═╝╚═╝  ╚═══╝

  ██████╗ ███████╗    ███████╗ ██████╗ ██████╗     ████████╗██╗  ██╗ █████╗ ████████╗
 ██╔═══██╗██╔════╝    ██╔════╝██╔═══██╗██╔══██╗    ╚══██╔══╝██║  ██║██╔══██╗╚══██╔══╝
 ██║   ██║███████╗    █████╗  ██║   ██║██████╔╝       ██║   ███████║███████║   ██║
 ██║   ██║╚════██║    ██╔══╝  ██║   ██║██╔══██╗       ██║   ██╔══██║██╔══██║   ██║
 ╚██████╔╝███████║    ██║     ╚██████╔╝██║  ██║       ██║   ██║  ██║██║  ██║   ██║
  ╚═════╝ ╚══════╝    ╚═╝      ╚═════╝ ╚═╝  ╚═╝       ╚═╝   ╚═╝  ╚═╝╚═╝  ╚═╝   ╚═╝`.trim();

export function HomePage() {
  const { categories, loading: catLoading } = useCategories();
  const { projects, loading: projLoading } = useProjects({ perPage: 30, sort: "stars" });
  const { tools, loading: toolsLoading } = useAllTools();

  // Show top tools that have alternatives
  const popularTools = tools.filter((t) => t.alternative_count > 0).slice(0, 24);

  useDocumentMeta({
    title: "There's an OS for That - Open-Source Alternatives Directory",
    description: "Discover open-source alternatives to popular software. Curated from GitHub, organized by category.",
  });

  return (
    <div>
      {/* Hero */}
      <section className="pt-12 sm:pt-20 pb-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid sm:grid-cols-2 gap-8 sm:gap-14 items-start">
            {/* ASCII Art */}
            <div className="overflow-hidden">
              <pre className="ascii-art font-mono">{ASCII_ART}</pre>
            </div>

            {/* Description */}
            <div className="flex flex-col gap-6">
              <h1 className="text-sm text-text-secondary leading-relaxed">
                The open directory of open-source alternatives to popular software.
                Find what you need, skip the vendor lock-in.
              </h1>

              <StatsBar />
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-10">
        <div className="flex items-center gap-3 mb-4">
          <h2 className="text-[10px] uppercase tracking-widest text-text-muted">Categories</h2>
          <div className="flex-1 h-px bg-border" />
        </div>
        {catLoading ? <Spinner /> : <CategoryGrid categories={categories} />}
      </section>

      {/* Popular Alternatives */}
      {!toolsLoading && popularTools.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-10">
          <div className="flex items-center gap-3 mb-4">
            <h2 className="text-[10px] uppercase tracking-widest text-text-muted">Find Alternatives To</h2>
            <div className="flex-1 h-px bg-border" />
          </div>
          <div className="flex flex-wrap gap-2">
            {popularTools.map((t) => (
              <a
                key={t.slug}
                href={`/alternative/${t.slug}`}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(`/alternative/${t.slug}`);
                }}
                className="inline-flex items-center gap-1.5 border border-border px-3 py-1.5 text-xs text-text-secondary hover:text-accent hover:border-accent/40 transition-colors"
              >
                {t.name}
                <span className="text-[10px] text-text-muted tabular-nums">{t.alternative_count}</span>
              </a>
            ))}
          </div>
        </section>
      )}

      {/* Top Projects - Leaderboard */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-16">
        <div className="flex items-center gap-3 mb-4">
          <h2 className="text-[10px] uppercase tracking-widest text-text-muted">Top Projects</h2>
          <div className="flex-1 h-px bg-border" />
        </div>
        {projLoading ? <Spinner /> : <ProjectGrid projects={projects} />}
      </section>
    </div>
  );
}
